// PackageDetail.js
import React, { useState, useEffect, useMemo } from 'react';
import { useParams, Link } from 'react-router-dom';
import { getPackageLessons, getPackageDetail } from '../services/api';
import { getToken } from '../utils/auth';

function PackageDetail() {
  const { packageId } = useParams();
  const [packageDetail, setPackageDetail] = useState(null);
  const [lessons, setLessons] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    const token = getToken();
    if (!token) {
      window.location.href = '/login';
      return;
    }

    const fetchData = async () => {
      try {
        setLoading(true);

        const cachedPackageDetail = sessionStorage.getItem(`packageDetail_${packageId}`);
        const cachedPackageTimestamp = sessionStorage.getItem(`packageDetail_${packageId}_timestamp`);
        const isPackageCacheValid = cachedPackageTimestamp && (Date.now() - parseInt(cachedPackageTimestamp)) < 10 * 60 * 1000;

        const cachedLessons = sessionStorage.getItem(`lessons_${packageId}`);
        const cachedLessonsTimestamp = sessionStorage.getItem(`lessons_${packageId}_timestamp`);
        const isLessonsCacheValid = cachedLessonsTimestamp && (Date.now() - parseInt(cachedLessonsTimestamp)) < 10 * 60 * 1000;

        let detailData, lessonsData;

        if (cachedPackageDetail && isPackageCacheValid) {
          detailData = JSON.parse(cachedPackageDetail);
        } else {
          detailData = await getPackageDetail(packageId);
          sessionStorage.setItem(`packageDetail_${packageId}`, JSON.stringify(detailData));
          sessionStorage.setItem(`packageDetail_${packageId}_timestamp`, Date.now().toString());
        }

        if (cachedLessons && isLessonsCacheValid) {
          lessonsData = JSON.parse(cachedLessons);
        } else {
          lessonsData = await getPackageLessons(packageId);
          sessionStorage.setItem(`lessons_${packageId}`, JSON.stringify(lessonsData));
          sessionStorage.setItem(`lessons_${packageId}_timestamp`, Date.now().toString());
        }

        setPackageDetail(detailData);
        setLessons(lessonsData);
      } catch (err) {
        setError('Nepodarilo sa načítať dáta balíčka');
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [packageId]);

  // Zoradené lekcie - najnovšie hore
  const sortedLessons = useMemo(() => {
    return [...lessons].sort((a, b) => {
      const dateA = new Date(a.datum);
      const dateB = new Date(b.datum);
      return dateB - dateA; // Zostupné poradie - najnovšie prvé
    });
  }, [lessons]);

  const getStatusClass = (status) => {
    if (!status) return 'status-badge nezačal';

    const statusLower = status.toLowerCase();
    if (statusLower.includes('prebieha') || statusLower.includes('active')) {
      return 'status-badge prebieha';
    } else if (statusLower.includes('skončil') || statusLower.includes('completed') || statusLower.includes('ukončen')) {
      return 'status-badge skoncil';
    } else if (statusLower.includes('nezačal') || statusLower.includes('not started') || statusLower.includes('pending')) {
      return 'status-badge nezačal';
    }

    return 'status-badge nezačal';
  };

  const getLessonTitle = (nazov) => {
    if (!nazov) return 'Bez názvu';
    const parts = nazov.split(' ');
    if (parts.length <= 2) {
      return nazov;
    }
    return parts.slice(0, -2).join(' ');
  };

  const formatDateTime = (dateString) => {
    if (!dateString) return 'Neurčený dátum a čas';
    try {
      const date = new Date(dateString);
      
      // Ošetrenie neplatného dátumu
      if (isNaN(date.getTime())) {
        return dateString;
      }
      
      // Formát: DD.MM.YYYY o HH:MM
      const day = String(date.getDate()).padStart(2, '0');
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const year = date.getFullYear();
      const hours = String(date.getHours()).padStart(2, '0');
      const minutes = String(date.getMinutes()).padStart(2, '0');
      
      return `${day}.${month}.${year} o ${hours}:${minutes}`;
    } catch {
      return dateString;
    }
  };

  const handleLessonLinkClick = (link) => {
    if (link) {
      window.open(link, '_blank');
    }
  };

  if (loading) return <div className="loading">Načítavam detail balíčka...</div>;
  if (error) return <div className="error">{error}</div>;

  return (
    <div className="detail-container">
      {/* Spojená hlavička a detaily v jednom boxe */}
      <section className="package-details">
        <div className="package-header">
          <div className="header-top">
            <div className="package-title-section">
              {packageDetail && (
                <>
                  <span className={getStatusClass(packageDetail.status)}>
                    {packageDetail.status}
                  </span>
                  <h1>{packageDetail.nazov_balicka}</h1>
                </>
              )}
            </div>
            <Link to="/dashboard" className="back-btn">
              <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="currentColor" viewBox="0 0 16 16">
                <path fillRule="evenodd" d="M11.354 1.646a.5.5 0 0 1 0 .708L5.707 8l5.647 5.646a.5.5 0 0 1-.708.708l-6-6a.5.5 0 0 1 0-.708l6-6a.5.5 0 0 1 .708 0z" />
              </svg>
              Späť na dashboard
            </Link>
          </div>
        </div>

        {packageDetail && (
          <div className="package-info-three-columns">
            <div className="info-column">
              <div className="info-item">
                <span className="info-label">Odučené lekcie:</span>
                <span className="info-value">{packageDetail.oducene_lekcie}/{packageDetail.kupene_lekcie}</span>
              </div>
              <div className="info-item">
                <span className="info-label">Zostáva lekcií:</span>
                <span className="info-value">{packageDetail.kupene_lekcie - packageDetail.oducene_lekcie}</span>
              </div>
            </div>

            <div className="info-column">
              <div className="info-item">
                <span className="info-label">Predmet:</span>
                <span className="info-value">{packageDetail.predmet}</span>
              </div>
              <div className="info-item">
                <span className="info-label">Sadzba:</span>
                <span className="info-value">{packageDetail.sadzba_za_lekciu}€/lekcia</span>
              </div>
            </div>

            <div className="info-column">
              <div className="info-item">
                <span className="info-label">Telefón:</span>
                <span className="info-value">{packageDetail.parent_phone}</span>
              </div>
              <div className="info-item">
                <span className="info-label">Email:</span>
                <span className="info-value">{packageDetail.parent_email}</span>
              </div>
            </div>
          </div>
        )}
      </section>

      <main className="lessons-board">
        <h2 className="lessons-title">Odučené lekcie</h2>
        {sortedLessons.length > 0 ? (
          <div className="lessons-list">
            {sortedLessons.map((lesson) => (
              <article key={lesson.id} className="lesson-card">
                <h3>{getLessonTitle(lesson.nazov)}</h3>
                <div className="lesson-details">
                  <p><strong>Dátum:</strong> {formatDateTime(lesson.datum)}</p>
                  <p><strong>Sadzba: </strong>
                    {/* Fallback: ak je sadzba v lekcii 0, použite sadzbu z balíčka */}
                    {(lesson.sadzba_za_lekciu && lesson.sadzba_za_lekciu > 0)
                      ? `${lesson.sadzba_za_lekciu}€`
                      : `${packageDetail.sadzba_za_lekciu}€`
                    }
                  </p>
                </div>
                {lesson.link && (
                  <a
                    href="#"
                    className="calendar-link"
                    onClick={(e) => {
                      e.preventDefault();
                      handleLessonLinkClick(lesson.link);
                    }}
                  >
                    Odkaz na lekciu v Google Calendar
                  </a>
                )}
              </article>
            ))}
          </div>
        ) : (
          <div className="no-data">
            Žiadne odučené lekcie pre tento balíček
          </div>
        )}
      </main>
    </div>
  );
}

export default PackageDetail;